"use client";

import { useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { Play } from "lucide-react";
import { VideoModal } from "@/shared/components/ui/VideoModal";

interface Tour {
  id: string;
  thumbnail?: string;
  tag: string;
  title: string;
  body: string;
  accent: string;
  filter: string;
}

interface FeaturedTour {
  id: string;
  thumbnail?: string;
  tag: string;
  title: string;
  body: string;
  accent: string;
}

interface FilterOption {
  id: string;
  label: string;
}

const thumbnail = (id: string) => `https://i.ytimg.com/vi/${id}/hqdefault.jpg`;

function PlayBadge({ size = "md" }: { size?: "lg" | "md" }) {
  return (
    <span className={`dl-play dl-play--${size}`} aria-hidden="true">
      <Play className="h-5 w-5" fill="currentColor" strokeWidth={0} />
    </span>
  );
}

export function DemoLibrary({
  featured,
  tours,
  filters,
}: {
  featured: FeaturedTour;
  tours: Tour[];
  filters: FilterOption[];
}) {
  const [active, setActive] = useState("all");
  const [openId, setOpenId] = useState<string | null>(null);

  const visible = active === "all" ? tours : tours.filter((t) => t.filter === active);
  const playing = openId ? [...tours, featured].find((t) => t.id === openId) : null;

  return (
    <>
      {/* ── Hero ── */}
      <section className="dl-hero">
        <div className="container dl-hero-grid">
          <div className="dl-hero-copy">
            <span className="dl-tag">
              <span className="dl-tag-dot" aria-hidden="true" />
              Demo Library
            </span>
            <h1 className="dl-h1">
              See what SCM Sensei <span className="grad-text">can do</span>
            </h1>
            <p className="dl-sub">
              Watch the modes work on real supply chain problems. Diagnostics, RFPs and
              scorecards, spend, inventory and freight analysis, and how consultants are
              using Sensei to deliver in days what used to take weeks.
            </p>
            <div className="dl-actions">
              <Link href="/contact" className="btn btn-primary btn-lg">
                Book a walkthrough
              </Link>
              <Link href="/login" className="btn btn-ghost btn-lg">
                Start for free
              </Link>
            </div>
          </div>

          {/* ── Featured tour ── */}
          <button
            type="button"
            className="dl-feature"
            onClick={() => setOpenId(featured.id)}
            style={{ ["--dl-accent" as string]: featured.accent }}
          >
            <span className="dl-feature-media">
              <Image
                src={featured.thumbnail ?? thumbnail(featured.id)}
                alt=""
                fill
                priority
                sizes="(max-width: 1024px) 100vw, 60vw"
                className="dl-media-img"
              />
              <PlayBadge size="lg" />
              <span className="dl-duration">Watch the overview</span>
            </span>
            <span className="dl-feature-text">
              <span className="dl-card-tag">{featured.tag}</span>
              <span className="dl-feature-title">{featured.title}</span>
              <span className="dl-card-body">{featured.body}</span>
            </span>
          </button>
        </div>
      </section>

      {/* ── Tours grid ── */}
      <section className="dl-tours">
        <div className="container">
          <div className="dl-tours-head">
            <div>
              <h2 className="sec-title dl-tours-title">View product tours</h2>
              <p className="dl-tours-sub">
                Short walkthroughs of each module, and the people already running it.
              </p>
            </div>

            <div className="dl-filters" role="group" aria-label="Filter tours by topic">
              <span className="dl-filters-label">Filter by</span>
              {filters.map((f) => (
                <button
                  key={f.id}
                  type="button"
                  className={`dl-chip${active === f.id ? " is-active" : ""}`}
                  aria-pressed={active === f.id}
                  onClick={() => setActive(f.id)}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>

          {visible.length > 0 ? (
            <div className="dl-grid">
              {visible.map((tour) => (
                <button
                  key={tour.id}
                  type="button"
                  className="dl-card"
                  onClick={() => setOpenId(tour.id)}
                  style={{ ["--dl-accent" as string]: tour.accent }}
                >
                  <span className="dl-card-media">
                    <Image
                      src={thumbnail(tour.id)}
                      alt=""
                      fill
                      sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
                      className="dl-media-img"
                    />
                    <PlayBadge />
                  </span>
                  <span className="dl-card-text">
                    <span className="dl-card-tag">{tour.tag}</span>
                    <span className="dl-card-title">{tour.title}</span>
                    <span className="dl-card-body">{tour.body}</span>
                  </span>
                </button>
              ))}
            </div>
          ) : (
            <p className="dl-empty">No tours in this category yet.</p>
          )}
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="cta">
        <h2>
          Watch the demo, or <span className="grad-text">skip to the trial.</span>
        </h2>
        <p>
          Sensei is free to try, no credit card. Bring a spend file, an SKU list, or an RFP
          you have been putting off.
        </p>
        <div className="cta-actions">
          <Link href="/login" className="btn btn-primary btn-lg">
            Start for free
          </Link>
          <Link href="/contact" className="btn btn-ghost btn-lg">
            Book a walkthrough
          </Link>
        </div>
      </section>

      <VideoModal
        isOpen={playing !== null}
        onClose={() => setOpenId(null)}
        src={playing ? `https://www.youtube.com/embed/${playing.id}?rel=0` : ""}
        title={playing ? playing.title : "Video"}
      />
    </>
  );
}
